import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Alert,
  ScrollView,
} from '../components/rn';
import PrimaryActionButton from '../components/PrimaryActionButton';
import type { TextInput as RNTextInput } from 'react-native';
import { spacing, fontSize, ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { api, ApiError, AuthError, NetworkError } from '../services/api';
import { errorMessageKey } from '../services/errorMessage';

type Props = {
  phone: string;
  onVerify: (token: string, onboarded: boolean) => void;
};

export default function OtpScreen({ phone, onVerify }: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  // Sunucu numara başına 30 sn'de bir kod verir (D-030). İstemci aynı süreyi
  // gösterir; böylece kullanıcı reddedilecek bir isteği hiç göndermez.
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef<RNTextInput>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  /**
   * D-422 — sunucu hataları YALNIZ Türkçe metinlerdir ve makine-okunur kod
   * taşımaz; ham gösterilince Fransızca ya da Japonca arayüzdeki kullanıcı
   * giriş yolunda Türkçe okuyordu. Bu uç noktada durum kodları tek anlamlıdır,
   * o yüzden çeviri koda göre seçilir.
   */
  function otpErrorKey(e: unknown, fallback: string): string {
    // 401 burada uç-noktaya özeldir: `verify-otp` için "yanlış kod" demektir,
    // oturum bitişi değil. Geri kalanı ortak eşleyiciye bırakılır.
    if (e instanceof AuthError || (e instanceof ApiError && e.status === 401)) return 'otp.invalidCode';
    if (e instanceof ApiError && e.status === 429) return 'otp.tooManyAttempts';
    return errorMessageKey(e, fallback);
  }

  async function handleVerify() {
    setLoading(true);
    try {
      const { token, onboarded } = await api.verifyOtp(phone, otp);
      onVerify(token, onboarded);
    } catch (error) {
      if (error instanceof NetworkError) {
        Alert.alert(t('message.noConnection'), t('message.checkInternet'));
      } else {
        Alert.alert(t('common.error'), t(otpErrorKey(error, 'otp.verifyFailed')));
      }
      setOtp('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  }

  // Buton daha önce `onPress={() => {}}` idi: SMS gelmeyen kullanıcı basıyor,
  // hiçbir şey olmuyordu. Giriş yolundaki tek kurtarma kontrolü budur.
  async function handleResend() {
    if (resending || cooldown > 0) return;
    setResending(true);
    try {
      await api.sendOtp(phone);
      setCooldown(30);
      Alert.alert('', t('otp.resendSent'));
    } catch (e) {
      Alert.alert(t('common.error'), t(otpErrorKey(e, 'otp.resendFailed')));
    } finally {
      setResending(false);
    }
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View style={styles.inner}>
        <Text style={styles.title}>{t('otp.title')}</Text>
        <Text style={styles.subtitle}>{t('otp.sentTo', { phone })}</Text>

        <TextInput
          ref={inputRef}
          style={styles.input}
          placeholder="0000"
          placeholderTextColor={colors.gray400}
          keyboardType="number-pad"
          maxLength={4}
          value={otp}
          onChangeText={setOtp}
          autoFocus
        />

        <PrimaryActionButton
          testID="onboarding-otp-verify"
          label={t('otp.verify')}
          style={styles.continueButton}
          disabled={otp.length < 4 || loading}
          loading={loading}
          onPress={handleVerify}
        />

        <TouchableOpacity
          style={styles.resend}
          onPress={handleResend}
          disabled={resending || cooldown > 0}
          accessibilityRole="button"
          accessibilityState={{ disabled: resending || cooldown > 0 }}
          testID="otp-resend">
          <Text style={[styles.resendText, (resending || cooldown > 0) && styles.resendDisabled]}>
            {cooldown > 0 ? t('otp.resendWait', { n: cooldown }) : t('otp.resend')}
          </Text>
        </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  scrollContent: { flexGrow: 1 },
  inner: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xl * 2,
  },
  title: {
    fontSize: fontSize.xl,
    fontWeight: '700',
    color: colors.black,
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: fontSize.md,
    color: colors.gray600,
    marginBottom: spacing.xl,
  },
  input: {
    fontSize: fontSize.xl,
    color: colors.black,
    borderBottomWidth: 1,
    borderBottomColor: colors.black,
    paddingVertical: spacing.sm,
    marginBottom: spacing.xl,
    letterSpacing: 1,
  },
  continueButton: { marginBottom: spacing.md },
  resend: {
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  resendText: {
    fontSize: fontSize.md,
    color: colors.gray600,
  },
  resendDisabled: { opacity: 0.5 },
});
